import axios from "axios";
import { enqueue } from "@/lib/offlineQueue";
import { getStoredToken, clearStoredToken } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 responses, drop the token so the app re-prompts for login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearStoredToken();
      // Soft redirect — let the route guards handle it on next render
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/watch")) {
        window.dispatchEvent(new CustomEvent("crickpad-auth-expired"));
      }
    }
    return Promise.reject(err);
  },
);

export class OfflineQueuedError extends Error {
  constructor() {
    super("OFFLINE_QUEUED");
    this.name = "OfflineQueuedError";
    this.queued = true;
  }
}

function isNetworkError(e) {
  return !navigator.onLine || e?.code === "ERR_NETWORK" || !e?.response;
}

async function postQueueable(url, data, computeOptimistic) {
  try {
    return (await api.post(url, data)).data;
  } catch (e) {
    if (isNetworkError(e)) {
      enqueue({ url, method: "POST", data });
      const err = new OfflineQueuedError();
      if (computeOptimistic) {
        try { err.optimisticMatch = computeOptimistic(); } catch { /* ignore */ }
      }
      throw err;
    }
    throw e;
  }
}

async function putQueueable(url, data, computeOptimistic) {
  try {
    return (await api.put(url, data)).data;
  } catch (e) {
    if (isNetworkError(e)) {
      enqueue({ url, method: "PUT", data });
      const err = new OfflineQueuedError();
      if (computeOptimistic) {
        try { err.optimisticMatch = computeOptimistic(); } catch { /* ignore */ }
      }
      throw err;
    }
    throw e;
  }
}

export const createMatch = (data) => api.post("/matches", data).then((r) => r.data);
export const listMatches = () => api.get("/matches").then((r) => r.data);
export const getMatch = (id) => api.get(`/matches/${id}`).then((r) => r.data);
export const startInnings = (id, data) => api.post(`/matches/${id}/start-innings`, data).then((r) => r.data);
export const recordBall = (id, data, computeOptimistic) =>
  postQueueable(`/matches/${id}/ball`, data, computeOptimistic);
export const undoBall = (id, computeOptimistic) =>
  postQueueable(`/matches/${id}/undo`, {}, computeOptimistic);
export const setBowler = (id, bowler_id, computeOptimistic) =>
  postQueueable(`/matches/${id}/set-bowler`, { bowler_id }, computeOptimistic);
export const endInnings = (id) => api.post(`/matches/${id}/end-innings`).then((r) => r.data);
export const deleteMatch = (id) => api.delete(`/matches/${id}`).then((r) => r.data);
export const getPlayerStats = () => api.get("/players/stats").then((r) => r.data);
export const editBall = (id, innings_idx, ball_idx, data, computeOptimistic) =>
  putQueueable(`/matches/${id}/balls/${innings_idx}/${ball_idx}`, data, computeOptimistic);
export const exportCsvUrl = (id) => `${API}/matches/${id}/export.csv`;

// Team Library
export const listTeams = () => api.get("/teams").then((r) => r.data);
export const createTeam = (data) => api.post("/teams", data).then((r) => r.data);
export const updateTeam = (id, data) => api.put(`/teams/${id}`, data).then((r) => r.data);
export const deleteTeam = (id) => api.delete(`/teams/${id}`).then((r) => r.data);
